import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface WorkflowStepItem {
  number: number;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  highlight?: boolean;
  completed?: boolean;
}

@Component({
  selector: 'etxe-property-application-workflow-explanation',
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './property-application-workflow-explanation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyApplicationWorkflowExplanationComponent {
  readonly activeStep = input<number>(1);
  readonly acknowledged = output<void>();

  readonly steps = computed<WorkflowStepItem[]>(() => {
    const current = this.activeStep();
    return [
      {
        number: 1,
        title: 'Candidature initiale',
        subtitle: current === 1 ? 'Étape actuelle' : current > 1 ? 'Complétée' : 'Estimation indicative',
        description:
          "Vous envoyez un premier message d'accroche personnalisé et proposez une estimation indicative de votre taux d'honoraires.",
        icon: current > 1 ? 'check_circle' : 'send',
        highlight: current === 1,
        completed: current > 1,
      },
      {
        number: 2,
        title: 'Rendez-vous de visite',
        subtitle:
          current === 2
            ? 'Étape actuelle'
            : current > 2
              ? 'Visite effectuée'
              : 'Sur place avec le propriétaire',
        description:
          'Le propriétaire analyse les profils des agents candidats et convient directement d’un rendez-vous sur place pour vous faire visiter le bien.',
        icon: current > 2 ? 'check_circle' : 'calendar_today',
        highlight: current === 2,
        completed: current > 2,
      },
      {
        number: 3,
        title: 'Avis de valeur & offre finale',
        subtitle:
          current === 3
            ? 'Étape actuelle'
            : current > 3
              ? 'Avis et offre finale envoyés'
              : 'Après découverte de l’état réel du bien',
        description:
          'Après la visite physique, vous formalisez votre avis de valeur (prix recommandé, fourchette), fixez votre commission définitive (ajustable de ±1%) et vos engagements de services.',
        icon: current > 3 ? 'check_circle' : 'tune',
        highlight: current === 3,
        completed: current > 3,
      },
      {
        number: 4,
        title: 'Attribution du mandat',
        subtitle: current === 4 ? 'Étape actuelle' : 'Sélection finale par le propriétaire',
        description:
          'Le propriétaire compare les propositions finales et choisit l’agent immobilier avec lequel il signe le mandat de vente.',
        icon: 'handshake',
        highlight: current === 4,
        completed: false,
      },
    ];
  });

  readonly continueButtonText = computed(() => {
    return this.activeStep() === 3
      ? "J'ai compris, compléter mon avis de valeur et offre finale"
      : "J'ai compris, accéder à ma candidature";
  });

  onContinue(): void {
    this.acknowledged.emit();
  }
}
